import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconShoppingCart } from '@redteclab/icons'

import { Button } from 'base-ui'

import {
  CartIntermediate,
  makeParamsForCartIntermediate,
  useProductAddToCart,
} from '../../../../../cart-intermediate'
import { useDeviceDetectorContext } from '../../../../../device-detector'
import { Experiment } from '../../../../../experiments/components/Experiment'
import { useGetFreeGiftPromotionsExperiment } from '../../../../../experiments/components/ExperimentFreeGiftPromotion/useGetFreeGiftPromotionsExperiment'
import { ExperimentVariant } from '../../../../../experiments/components/ExperimentVariant'
import {
  EXPERIMENTS_TRACK_EVENT_FREE_GIFT_PROMOTION_ADD_TO_CART,
  EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
  EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT,
  experimentsTrackEvent,
} from '../../../../../experiments/eventTracking'
import { useGetExperimentForTenant } from '../../../../../experiments/hooks'
import { EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER } from '../../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_NAME_REMOVE_PACKSIZES_FOR_EXPA } from '../../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { EXPERIMENT_VARIATION } from '../../../../../experiments/model/EXPERIMENT_VARIATION'
import { useRemovePackagesForExpaExperimentIsExpaPresentInUrl } from '../../../../../experiments/remove-variants-for-expa-experiment/components/useIsExpaPresentInUrl'
import { useGlobalConfigContext } from '../../../../../global-config'
import { productAccessorIsSaeOffer } from '../../../../model/productAccessor/productAccessorIsSaeOffer'
import { useProductPromotion } from '../../../ProductDescription/ProductDescriptionPromotions'
import { useProductPageContext } from '../../../productPageContext'

import { useBuildAddToCartParams } from './useBuildAddToCartParams'

import styles from './ProductAddToCartCta.module.css'

const useShouldHideVariantsForRemovePackSizeExperiment = (): {
  shouldHideVariantsForRemovePackSizeExperiment: boolean
} => {
  const removePackSizesExperiment = useGetExperimentForTenant(
    EXPERIMENT_NAME_REMOVE_PACKSIZES_FOR_EXPA,
  )
  const isExpaPresentInUrl =
    useRemovePackagesForExpaExperimentIsExpaPresentInUrl()

  return {
    shouldHideVariantsForRemovePackSizeExperiment:
      isExpaPresentInUrl && removePackSizesExperiment.isEnabled,
  }
}

const useTrackAddToCartEvents = (): {
  trackAddToCartButtonFreeGiftPromotionsExperiment: () => void
  trackAddToCartButtonMarketPlaceProduct: () => void
  trackAddToCartButtonRemoveVariantsExperiment: () => void
} => {
  const globalConfig = useGlobalConfigContext()
  const { currentOffer } = useProductPageContext()
  const { hasPromotion } = useProductPromotion()
  const { isMobile } = useDeviceDetectorContext()
  const freeGiftPromotionsExperiment =
    useGetFreeGiftPromotionsExperiment(isMobile)
  const { shouldHideVariantsForRemovePackSizeExperiment } =
    useShouldHideVariantsForRemovePackSizeExperiment()

  const trackAddToCartButtonMarketPlaceProduct = (): void => {
    const isMarketPlace =
      currentOffer && !productAccessorIsSaeOffer(currentOffer)

    if (isMarketPlace) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_MP_PRODUCT,
      )
    }
  }

  const trackAddToCartButtonFreeGiftPromotionsExperiment = (): void => {
    if (hasPromotion && freeGiftPromotionsExperiment.isEnabled) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_FREE_GIFT_PROMOTION_ADD_TO_CART,
      )
    }
  }

  const trackAddToCartButtonRemoveVariantsExperiment = (): void => {
    if (shouldHideVariantsForRemovePackSizeExperiment) {
      experimentsTrackEvent(
        globalConfig,
        EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
      )
    }
  }

  return {
    trackAddToCartButtonFreeGiftPromotionsExperiment,
    trackAddToCartButtonMarketPlaceProduct,
    trackAddToCartButtonRemoveVariantsExperiment,
  }
}

export const AddToCartButton: FC = () => {
  const {
    trackAddToCartButtonFreeGiftPromotionsExperiment,
    trackAddToCartButtonMarketPlaceProduct,
    trackAddToCartButtonRemoveVariantsExperiment,
  } = useTrackAddToCartEvents()

  const {
    currentOffer,
    currentPrescriptionType,
    currentVariant,
    quantitySelectionControls: { quantity },
  } = useProductPageContext()

  const { disableCta } = useBuildAddToCartParams()

  const {
    addToCartResponseData,
    handleAddToCart,
    isAddProductToCartSuccessful,
  } = useProductAddToCart({
    currentPrescriptionType,
    offerId: currentOffer?.offerId,
    quantity,
  })

  return (
    <Experiment names={[EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER]}>
      <ExperimentVariant name={EXPERIMENT_VARIATION.DEFAULT}>
        <Button
          className={clsx(
            'w-full',
            'whitespace-pre-wrap',
            styles['product-add-to-cart-cta__button'],
          )}
          data-qa-id="add-to-cart-button"
          disabled={disableCta}
          onClick={(): void => {
            trackAddToCartButtonFreeGiftPromotionsExperiment()
            trackAddToCartButtonMarketPlaceProduct()
            trackAddToCartButtonRemoveVariantsExperiment()
          }}
          startIcon={<IconShoppingCart />}
          type="submit"
        >
          <FormattedMessage id="general.button.label.addToCart" />
        </Button>
      </ExperimentVariant>

      <ExperimentVariant name={EXPERIMENT_VARIATION.V1}>
        <Button
          className={clsx(
            'w-full',
            'whitespace-pre-wrap',
            styles['product-add-to-cart-cta__button'],
          )}
          data-qa-id="add-to-cart-button"
          disabled={disableCta}
          onClick={() => {
            trackAddToCartButtonFreeGiftPromotionsExperiment()
            trackAddToCartButtonMarketPlaceProduct()
            trackAddToCartButtonRemoveVariantsExperiment()
            void handleAddToCart()
          }}
          startIcon={<IconShoppingCart />}
          type="button"
        >
          <FormattedMessage id="general.button.label.addToCart" />
        </Button>
        <CartIntermediate
          {...makeParamsForCartIntermediate({
            isAddProductToCartSuccessful,
            offer: addToCartResponseData?.offer,
            prescriptionType: currentPrescriptionType,
            product: addToCartResponseData?.product ?? currentVariant,
            quantity: addToCartResponseData?.quantity,
          })}
        />
      </ExperimentVariant>
    </Experiment>
  )
}
